// src/services/towerSiteBOQService.js
import api from '../config/axios';

/**
 * Tower Site BOQ Service
 * Frontend API service for Tower Site BOQ operations
 * 
 * Endpoints:
 * - GET    /api/tower-site-boqs/tower-site/{towerSiteId}           - Get all BOQs
 * - GET    /api/tower-site-boqs/tower-site/{towerSiteId}/sitac     - Get SITAC BOQs
 * - GET    /api/tower-site-boqs/tower-site/{towerSiteId}/cme       - Get CME BOQs
 * - GET    /api/tower-site-boqs/{id}                               - Get BOQ by ID
 * - GET    /api/tower-site-boqs/tower-site/{towerSiteId}/status    - Get BOQ status
 * - POST   /api/tower-site-boqs                                    - Save BOQ
 * - DELETE /api/tower-site-boqs/{id}                               - Delete BOQ
 */

const BASE_URL = '/tower-site-boqs';

const towerSiteBOQService = {
  
  /**
   * Get all BOQs for a tower site
   * Returns both SITAC and CME BOQs with items
   * 
   * @param {number} towerSiteId - Tower site ID
   * @returns {Promise<Array>} Array of BOQ objects
   */
  getBOQsByTowerSiteId: async (towerSiteId) => {
    try {
      console.log('📦 Fetching BOQs for tower site:', towerSiteId);
      const response = await api.get(`${BASE_URL}/tower-site/${towerSiteId}`);
      console.log('✅ BOQs loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching BOQs:', error);
      throw error;
    }
  },
  
  /**
   * Get SITAC BOQs for a tower site
   * 
   * @param {number} towerSiteId - Tower site ID
   * @returns {Promise<Array>} Array of SITAC BOQ objects
   */
  getSitacBOQsByTowerSiteId: async (towerSiteId) => {
    try {
      console.log('📦 Fetching SITAC BOQs for tower site:', towerSiteId);
      const response = await api.get(`${BASE_URL}/tower-site/${towerSiteId}/sitac`);
      console.log('✅ SITAC BOQs loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching SITAC BOQs:', error);
      throw error;
    }
  },
  
  /**
   * Get CME BOQs for a tower site
   * 
   * @param {number} towerSiteId - Tower site ID
   * @returns {Promise<Array>} Array of CME BOQ objects
   */
  getCmeBOQsByTowerSiteId: async (towerSiteId) => {
    try {
      console.log('📦 Fetching CME BOQs for tower site:', towerSiteId);
      const response = await api.get(`${BASE_URL}/tower-site/${towerSiteId}/cme`);
      console.log('✅ CME BOQs loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching CME BOQs:', error);
      throw error;
    }
  },
  
  /**
   * Get single BOQ by ID
   * 
   * @param {number} boqId - BOQ ID
   * @returns {Promise<Object>} BOQ object with items
   */
  getBOQById: async (boqId) => {
    try {
      console.log('📦 Fetching BOQ:', boqId);
      const response = await api.get(`${BASE_URL}/${boqId}`);
      console.log('✅ BOQ loaded:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching BOQ:', error);
      throw error;
    }
  },
  
  /**
   * Get BOQ status for a tower site
   * Used to check if SITAC/CME BOQ exists
   * 
   * @param {number} towerSiteId - Tower site ID
   * @returns {Promise<Object>} Status object { hasSitacBOQ, hasCmeBOQ }
   */
  getBOQStatus: async (towerSiteId) => {
    try {
      console.log('🔍 Checking BOQ status for tower site:', towerSiteId);
      const response = await api.get(`${BASE_URL}/tower-site/${towerSiteId}/status`);
      console.log('✅ BOQ status:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error checking BOQ status:', error);
      throw error;
    }
  },
  
  /**
   * Save Tower Site BOQ (Create or Update)
   * 
   * @param {Object} request - BOQ save request
   * @param {number} request.towerSiteId - Tower site ID
   * @param {number} request.budgetCategoryId - Budget category ID
   * @param {string} request.boqType - BOQ type (SITAC or CME)
   * @param {string} request.remarks - Optional remarks
   * @param {Array} request.items - Array of BOQ items
   * @param {number} request.items[].itemId - Item ID from master data
   * @param {number} request.items[].volume - Quantity/volume
   * @param {string} request.items[].remarks - Optional item remarks
   * @param {number} request.items[].sequenceOrder - Display order
   * @returns {Promise<Object>} Saved BOQ object
   */
  saveBOQ: async (request) => {
    try {
      console.log('💾 Saving BOQ:', request);
      const response = await api.post(BASE_URL, request);
      console.log('✅ BOQ saved successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error saving BOQ:', error);
      throw error;
    }
  },
  
  /**
   * Delete Tower Site BOQ
   * 
   * @param {number} boqId - BOQ ID
   * @returns {Promise<void>}
   */
  deleteBOQ: async (boqId) => {
    try {
      console.log('🗑️ Deleting BOQ:', boqId);
      await api.delete(`${BASE_URL}/${boqId}`);
      console.log('✅ BOQ deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting BOQ:', error);
      throw error;
    }
  },
  
  /**
   * Check if SITAC BOQ exists
   * Convenience method wrapping getBOQStatus
   * 
   * @param {number} towerSiteId - Tower site ID
   * @returns {Promise<boolean>} True if SITAC BOQ exists
   */
  checkSitacBOQExists: async (towerSiteId) => {
    try {
      const status = await towerSiteBOQService.getBOQStatus(towerSiteId);
      return status.hasSitacBOQ;
    } catch (error) {
      console.error('❌ Error checking SITAC BOQ existence:', error);
      return false;
    }
  },
  
  /**
   * Check if CME BOQ exists
   * Convenience method wrapping getBOQStatus
   * 
   * @param {number} towerSiteId - Tower site ID
   * @returns {Promise<boolean>} True if CME BOQ exists
   */
  checkCmeBOQExists: async (towerSiteId) => {
    try {
      const status = await towerSiteBOQService.getBOQStatus(towerSiteId);
      return status.hasCmeBOQ;
    } catch (error) {
      console.error('❌ Error checking CME BOQ existence:', error);
      return false;
    }
  }
};

// ============= Wrapper Functions for Compatibility =============

/**
 * Get BOQs by tower site ID and type
 * Wrapper function that calls appropriate method based on type
 * 
 * @param {number} towerSiteId - Tower site ID
 * @param {string} boqType - BOQ type ('SITAC' or 'CME')
 * @returns {Promise<Array>} Array of BOQ objects
 */
export const getBOQsByTowerSiteIdAndType = async (towerSiteId, boqType) => {
  if (boqType === 'SITAC') {
    return towerSiteBOQService.getSitacBOQsByTowerSiteId(towerSiteId);
  } else if (boqType === 'CME') {
    return towerSiteBOQService.getCmeBOQsByTowerSiteId(towerSiteId);
  } else {
    throw new Error(`Invalid BOQ type: ${boqType}. Must be 'SITAC' or 'CME'`);
  }
};

/**
 * Get single BOQ by tower site, category, and type
 * 
 * @param {number} towerSiteId - Tower site ID
 * @param {number} categoryId - Budget category ID
 * @param {string} boqType - BOQ type ('SITAC' or 'CME')
 * @returns {Promise<Object>} BOQ object or null if not found
 */
export const getBOQByTowerSiteAndCategoryAndType = async (towerSiteId, categoryId, boqType) => {
  try {
    const boqs = await getBOQsByTowerSiteIdAndType(towerSiteId, boqType);
    const boq = boqs.find(b => b.budgetCategoryId === categoryId);
    
    if (!boq) {
      console.warn(`⚠️ BOQ not found for tower site ${towerSiteId}, category ${categoryId}, type ${boqType}`);
      return null;
    }
    
    return boq;
  } catch (error) {
    console.error('❌ Error getting BOQ by tower site, category and type:', error);
    throw error;
  }
};

// ============= Export Default with Wrapper Functions =============

export default {
  ...towerSiteBOQService,
  getBOQsByTowerSiteIdAndType,
  getBOQByTowerSiteAndCategoryAndType
};